import { computed, inject, Injectable } from '@angular/core';
import { SelectListOption } from '@bmw-ds/components';

import { useI18n } from '../../i18n/services/i18n.service';
import { Customer } from '../model/customer.model';
import { FoundCustomer } from '../model/found-customer.model';
import { IdentityField } from '../model/identity-field.model';
import { FeatureVisibilityService } from './feature-visibility.service';

export enum IdCreationStatus {
  SUCCESSFUL = 'SUCCESSFUL',
  LOADING = 'LOADING',
  FAILED = 'FAILED',
}

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  readonly featureVisibilityService = inject(FeatureVisibilityService);
  protected readonly i18n = useI18n();

  private availableCustomerCountryCodes = [
    'AL',
    'AD',
    'AM',
    'AT',
    'AZ',
    'BY',
    'BE',
    'BA',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'GE',
    'DE',
    'GR',
    'HU',
    'IS',
    'IE',
    'IT',
    'KZ',
    'XK',
    'LV',
    'LI',
    'LT',
    'LU',
    'MT',
    'MD',
    'MC',
    'ME',
    'NL',
    'MK',
    'NO',
    'PL',
    'PT',
    'RO',
    'RU',
    'SM',
    'RS',
    'SK',
    'SI',
    'ES',
    'SE',
    'CH',
    'TR',
    'UA',
    'GB',
    'VA',
  ];

  readonly countries = computed((): SelectListOption[] => {
    return this.availableCustomerCountryCodes.map((countryCode) => {
      const translatedCountry = this.i18n.enum(countryCode, `country`);
      return {
        id: countryCode,
        label: `${translatedCountry} (${countryCode})`,
      };
    });
  });

  /**
   * Returns the full name of a customer, taking into account the visibility of fields based on the user's country.
   *
   * If this is called in a computed context, it will be re-evaluated when the country changes.
   */
  fullName(customer: Customer | FoundCustomer): string {
    return [
      this.featureVisibilityService.isHidden(IdentityField.FIRST_NAME) ? '' : customer.firstName,
      this.featureVisibilityService.isHidden(IdentityField.MIDDLE_NAME) ? '' : customer.middleName,
      this.featureVisibilityService.isHidden(IdentityField.LAST_NAME) ? '' : customer.lastName,
      this.featureVisibilityService.isHidden(IdentityField.SUFFIX) ? '' : customer.suffix,
    ]
      .filter(Boolean) // Remove empty parts
      .join(' ');
  }
}
